import Breadcrumb from "Components/UI/Breadcrumb";
import Spinners from "Components/UI/Spinner";
import PaginatedTable from "Components/Tables/PaginatedTable";
import React, { useEffect, useState } from "react";
import { Card, CardBody, Container } from "reactstrap";
import useTableFilters from "helpers/hooks/useTableFilters";
import { generateQueryParams } from "helpers/queryParams";
import { Link } from "react-router-dom";
import convertDateFormat from "Components/Date/DateFormat";
import DeleteModal from "Components/Modals/DeleteModal";
import { getPackages, deletePackage } from 'helpers/API Calls/Operations/packages';

interface PackageData {
  id: number;
  package_name: string;
  sku: string;
  price: number;
  main_category_id: number;
  direct_products: any[];
  groups: any[];
  status: string;
  created_at: string;
  updated_at: string;
  main_category: {
    category_name: string;
  } | null;
}

const tableHead = [
  {id: 1, name: "#", isSortable: true, key: "id"},
  {id: 2, name: "Package", isSortable: true, key: "package_name"},
  {id: 3, name: "SKU", isSortable: false, key: "sku"},
  {id: 4, name: "Price", isSortable: true, key: "price"},
  {id: 5, name: "Category", isSortable: false, key: "main_category_id"},
  {id: 6, name: "Products", isSortable: false, key: "direct_products"},
  {id: 7, name: "Created", isSortable: true, key: "created_at"},
  {id: 8, name: "Updated", isSortable: true, key: "updated_at"},
  {id: 9, name: "Actions"}
];

function Packages() {
  const [isLoading, setIsLoading] = useState(true);
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<PackageData | null>(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const { filters, updateFilter } = useTableFilters();

  const fetchData = React.useCallback(async () => {
    setIsLoading(true);
    const queryString = generateQueryParams(filters);
    const response = await getPackages(queryString);
    if (response.success) {
      setPackages(response.data.items);
      setPagination(response.data.pagination);
    }
    setIsLoading(false);
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = (item: PackageData) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (selectedItem) {
      const response = await deletePackage(selectedItem.id);
      if (response.success) {
        setDeleteModal(false);
        setSelectedItem(null);
        fetchData();
      }
    }
  };

  const onCloseClick = () => {
    setDeleteModal(false);
    setSelectedItem(null);
  };

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb items={[{ label: "Packages" }]} />
          {isLoading ? <div className="position-relative vh-100"><Spinners/></div>
          :
          <Card>
            <CardBody>
              <PaginatedTable
                tableHead={tableHead}
                pagination={pagination}
                isAddButton={true}
                buttonText="Add New Package"
                to="/packages/create"
                updateFilter={updateFilter}
                filters={filters}
              > 
                {packages.length > 0 && packages.map((item) => (
                  <tr key={item?.id}>
                    <td>{item?.id}</td>
                    <td>{item?.package_name}</td>
                    <td>{item?.sku}</td>
                    <td>£{item?.price}</td>
                    <td>{item?.main_category?.category_name || '-'}</td>
                    <td>
                      {item?.direct_products?.length + 
                       item?.groups?.reduce((acc, group) => acc + group.products.length, 0)} items
                    </td>
                    <td>{convertDateFormat(item.created_at)}</td>
                    <td>{convertDateFormat(item.updated_at)}</td>
                    <td>
                      <div>
                        <Link to={`${item.id}/edit`} className="btn btn-primary btn-sm mx-1">
                          <i className="bx bx-pencil"/>
                        </Link>
                        <button 
                          className="btn btn-danger btn-sm" 
                          onClick={() => handleDeleteClick(item)}
                        >
                          <i className="mdi mdi-trash-can-outline"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </PaginatedTable>
            </CardBody>
          </Card>}
        </Container>
      </div>
      
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={onCloseClick}
        message={`${selectedItem?.package_name} package?`}
      />
    </>
  );
}

export default Packages;
